import { useEffect, useState, useRef } from 'react';
import { LightBulbIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

interface GuidedHintToastProps {
  hint: string;
  type?: 'suggestion' | 'warning' | 'praise'; // Type of hint
  onClose: () => void;
  duration?: number; // milliseconds, default 10000
}

export default function GuidedHintToast({ hint, type = 'suggestion', onClose, duration = 6000 }: GuidedHintToastProps) {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(100);
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isDismissing, setIsDismissing] = useState(false);
  const toastRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  
  // Color scheme based on type
  const colorScheme = type === 'praise' 
    ? {
        bg: 'from-green-50/90 to-emerald-50/90',
        border: 'border-green-300/70',
        iconBg: 'bg-green-400',
        progress: 'bg-green-400/90',
        title: 'text-green-900',
        icon: '✅'
      }
    : {
        bg: 'from-amber-50/90 to-yellow-50/90',
        border: 'border-amber-300/70',
        iconBg: 'bg-amber-400',
        progress: 'bg-amber-400/90',
        title: 'text-amber-900',
        icon: '💡'
      };

  // Keep latest onClose without retriggering timers
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    // Trigger entrance animation
    setTimeout(() => setIsVisible(true), 10);

    // Progress bar animation
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);

      if (remaining === 0) {
        clearInterval(interval);
      }
    }, 50);

    // Auto-close after duration
    const timeout = setTimeout(() => {
      setIsDismissing(true);
      setIsVisible(false);
      setTimeout(() => onCloseRef.current(), 300); // Wait for exit animation
    }, duration);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [duration, hint]);

  const handleClose = () => {
    setIsDismissing(true);
    setIsVisible(false);
    setTimeout(() => onCloseRef.current(), 300); // Wait for exit animation
  };

  // Handle swipe right to dismiss (mobile-friendly, iOS/Android style)
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart({
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart) return;
    
    const currentX = e.touches[0].clientX;
    const diffX = currentX - touchStart.x;
    
    // Only allow swipe right (positive offset)
    if (diffX > 0) {
      setSwipeOffset(diffX);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart === null) return;
    
    const touchEnd = {
      x: e.changedTouches[0].clientX,
      y: e.changedTouches[0].clientY
    };
    
    const diffX = touchEnd.x - touchStart.x;
    const diffY = touchEnd.y - touchStart.y;
    
    // Swipe right (diffX > 80px and mostly horizontal)
    if (diffX > 80 && Math.abs(diffY) < 50) {
      handleClose();
    } else {
      // Reset position if swipe wasn't far enough
      setSwipeOffset(0);
    }
    
    setTouchStart(null);
  };

  return (
    <div
      ref={toastRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      className={`fixed top-3 md:top-4 left-1/2 -translate-x-1/2 w-[calc(100%-1.5rem)] md:w-[calc(100%-2rem)] max-w-sm md:max-w-md bg-gradient-to-r ${colorScheme.bg} backdrop-blur-sm border ${colorScheme.border} rounded-xl shadow-xl md:shadow-2xl z-[9999] transition-all ease-out overflow-hidden ${
        isDismissing ? 'duration-300' : swipeOffset > 0 ? 'duration-75' : 'duration-300'
      }`}
      style={{
        transform: `translate(-50%, ${isVisible ? '0' : '-100%'}) translateX(${swipeOffset}px)`,
        opacity: isVisible ? Math.max(0, 1 - swipeOffset / 200) : 0
      }}
      role="alert"
      aria-live="polite"
    >
      {/* Progress bar - thinner in mobile */}
      <div className={`absolute bottom-0 left-0 h-0.5 md:h-1 ${colorScheme.progress} ${progress < 100 ? 'rounded-br-xl' : ''} rounded-bl-xl transition-all duration-100`} style={{ width: `${progress}%` }} />

      {/* Swipe indicator (subtle visual cue for horizontal swipe) */}
      <div className="absolute top-2 left-1/2 -translate-x-1/2 w-12 h-1 bg-gray-300 rounded-full" />

      <div className="p-3 md:p-4 pt-6 pr-10 md:pr-12">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="flex-shrink-0 mt-0.5">
            <div className={`w-7 h-7 md:w-8 md:h-8 ${colorScheme.iconBg} rounded-full flex items-center justify-center animate-pulse`}>
              <LightBulbIcon className="w-4 h-4 md:w-5 md:h-5 text-white" />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className={`text-xs md:text-sm font-semibold ${colorScheme.title} mb-0.5 md:mb-1`}>{colorScheme.icon} {t('scenario.session.hints.coachingHint')}</div>
            <p className="text-xs md:text-sm text-gray-800 leading-relaxed">{hint}</p>
          </div>

          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-5 md:top-6 right-2 md:right-3 text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="Close hint"
          >
            <XMarkIcon className="w-4 h-4 md:w-5 md:h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
